import React, { Component } from "react";
import { API_CONFIG } from "../../config/api_config";
import PageProgressBar from "../template_page/page_progress_bar/page_progress_bar";
import TemplatePage from "../template_page/template_page";
import { HIGH_LEVEL_CONFIG } from "../../config/high_level_config";
import TemplatePageLeftContent from "../template_page/template_page_left_content/template_page_left_content";
import "./page_dispatcher.scss";
import { LEFT_CONTENT_CLASSES, PAGES_CLASSES } from "../../config/pages";
import { extraSmallLayout } from "../../utils/device";
import { BreadcrumbPiece } from "../../utils/menu";


interface PageDispatcherProps {
    mapping: Map<string, string>;
    path: string;
    language: string;
    query: any;
    history: any;

    menuColorCallback: (color: string) => void;
    breadcrumbCallback: (breadcrumb: BreadcrumbPiece[]) => void;
    changeLanguage: (language: string) => void;
}

const MIN_LOADING_TIME_MS = 3000;


export function computeBackendPath(frontendPath: string, mapping: Map<string, string>): string {
    // After the mapping has been completed, we compute the page url
    let path = frontendPath;
    let pagePath: string | undefined = mapping.get(path);
    if (!pagePath && (path.startsWith("/it/") || path.startsWith("/en/"))) {
        pagePath = mapping.get(path.substr(3));
    }

    if (pagePath && !pagePath.startsWith("/")) {
        pagePath = "/" + pagePath;
    }

    if (!pagePath) {
        pagePath = frontendPath;
    }
    return pagePath;
}

class PageDispatcher extends Component<PageDispatcherProps> {

    // true while the logo is animating, we need a static variable to avoid restarting the logo animation
    // if the PageDispatcher component is recreated while the animation is playing
    private static animatingLogo: boolean = false;


    state: {
        logoAnimationCompleted: boolean,
        languageShown: string,
        backendPath: string,
        PageClass?: any,
        LeftContentClass?: any
    };

    videoRef = React.createRef<HTMLVideoElement>();


    constructor(props: PageDispatcherProps) {
        super(props);

        this.state = {
            logoAnimationCompleted: false,
            languageShown: HIGH_LEVEL_CONFIG.DEFAULT_LANGUAGE,
            backendPath: ""
        };
    }


    computePagePath() {
        if (this.props.mapping) {
            this.resetLogoAnimation();

            console.log("[LOG]: Computing page path for " + this.props.path);
            let mapping = new Map(Object.entries(this.props.mapping));

            let pagePath = computeBackendPath(this.props.path, mapping);
            
            let pagePrefix = Array.from(PAGES_CLASSES.keys()).find(prefix => pagePath.startsWith(prefix));
            let PageClass = pagePrefix ? PAGES_CLASSES.get(pagePrefix) : TemplatePage;
            let LeftContentClass = pagePrefix ? LEFT_CONTENT_CLASSES.get(pagePrefix) : TemplatePageLeftContent;
            // We have to add the language between the content type and the id
            if (this.props.path == "/") {
                pagePath = `/home_page/${this.props.language ? this.props.language : "it"}/`;
            } else {
                pagePath = `${pagePrefix ? pagePrefix : "/"}${this.props.language ? this.props.language : "it"}/${pagePath.substring(pagePrefix ? pagePrefix.length : 1)}`;
            }
            this.setState({
                PageClass: PageClass,
                LeftContentClass: LeftContentClass,
                backendPath: pagePath
            });
        }
    }


    componentDidMount(): void {
        this.computePagePath();
    }


    resetLogoAnimation() {

        // if the logo is not already animating, let's start the logo animation
        if (!PageDispatcher.animatingLogo) {
            this.setState({
                logoAnimationCompleted: false
            });
            PageDispatcher.animatingLogo = true;

            if (this.videoRef.current) {
                this.videoRef.current.play().then(() => { });
            }
        }

        // After the logo animation has completed, we show the page contents
        setTimeout(() => {
            PageDispatcher.animatingLogo = false;

            this.setState({
                logoAnimationCompleted: true
            });

        }, MIN_LOADING_TIME_MS);
    }


    componentDidUpdate(prevProps: Readonly<PageDispatcherProps>, prevState: Readonly<{}>, snapshot?: any): void {
        if (this.props.path !== prevProps.path || this.props.language !== prevProps.language || this.props.mapping !== prevProps.mapping) {
            this.computePagePath();
        }

        if (this.props.path !== prevProps.path) {
            window.scrollTo({ top: 0, behavior: "auto" });
        }
    }

    pageLoaded = () => {
        this.setState({
            logoAnimationCompleted: true
        });
    };


    render(): React.ReactElement {

        let PageClass = this.state.PageClass;

        let layoutColumns = extraSmallLayout() ? 1 : 2;

        return (<>
            {/* the animation of the logo at the center and the progress bar
                this will be shown while we don't have the page loaded */}
            
            { window.location.pathname !== '/' && window.location.pathname !== '' && (
            <div className={`page-animation ${this.state.logoAnimationCompleted ? "" : "logo-animation"}`}>
                <PageProgressBar />
                <video id="logo-video" ref={this.videoRef} autoPlay={true} muted={true} playsInline={true}>
                    <source src="/luiss_logo_animation.mp4" type="video/mp4" />
                </video>
            </div>
            )}

            {
                /* the page with all its contents */
                PageClass &&
                <PageClass menuColorCallback={this.props.menuColorCallback} breadcrumbCallback={this.props.breadcrumbCallback}
                    language={this.props.language} pageLoaded={this.pageLoaded}
                    query={this.props.query} path={this.props.path} navigate={this.props.history.push} changeLanguage={this.props.changeLanguage}
                    logoAnimationCompleted={this.state.logoAnimationCompleted} layoutColumns={layoutColumns}
                    leftContentClass={this.state.LeftContentClass} url={API_CONFIG.BASE_URL + this.state.backendPath} />
            }
        </>);
    }
}

export default PageDispatcher;
